import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import LazyImage from '../../../components/LazyImage';
import { supabase } from '../../../lib/supabase';
import { friendDuelActivityService } from '../../../services/friendDuelActivityService';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const FriendDuelActivityFeed = ({ userId, onDuelClick, onJoinDuel }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastViewedAt, setLastViewedAt] = useState(localStorage.getItem('lastViewedDuelActivity') || new Date()?.toISOString());
  const [friendIds, setFriendIds] = useState([]);

  // Load friend IDs
  useEffect(() => {
    const loadFriendIds = async () => {
      try {
        const { data: friendships } = await supabase
          ?.from('friendships')
          ?.select('friend_id')
          ?.eq('user_id', userId)
          ?.eq('status', 'accepted');

        setFriendIds(friendships?.map(f => f?.friend_id) || []);
      } catch (error) {
        console.error('Error loading friend IDs:', error);
      }
    };

    if (userId) {
      loadFriendIds();
    }
  }, [userId]);

  // Load activity feed
  const loadActivity = async () => {
    setLoading(true);
    try {
      const activityData = await friendDuelActivityService?.getFriendDuelActivity(userId, 20);
      setActivities(activityData || []);

      // Load unread count
      const count = await friendDuelActivityService?.getUnreadActivityCount(userId, lastViewedAt);
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading friend duel activity:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      loadActivity();
    }
  }, [userId]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!userId || friendIds?.length === 0) return;

    const subscription = friendDuelActivityService?.subscribeFriendDuelActivity(
      userId,
      friendIds,
      {
        onDuelCreated: async (payload) => {
          // Reload activity feed
          await loadActivity();
          // Increment unread count
          setUnreadCount(prev => prev + 1);
        },
        onProofUploaded: async (payload) => {
          // Reload activity feed
          await loadActivity();
          // Increment unread count
          setUnreadCount(prev => prev + 1);
        },
        onVerificationComplete: async (payload) => {
          // Reload activity feed
          await loadActivity();
        }
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, [userId, friendIds]);

  // Mark as viewed
  const markAsViewed = () => {
    const now = new Date()?.toISOString();
    setLastViewedAt(now);
    localStorage.setItem('lastViewedDuelActivity', now);
    setUnreadCount(0);
  };

  // Handle join duel
  const handleJoinDuel = async (duelId) => {
    try {
      await friendDuelActivityService?.joinDuel(duelId, userId);
      await loadActivity();
      onJoinDuel?.(duelId);
    } catch (error) {
      console.error('Error joining duel:', error);
    }
  };

  // Get activity type icon and text
  const getActivityInfo = (activity) => {
    if (activity?.activity?.latestProof) {
      const proof = activity?.activity?.latestProof;
      if (proof?.auto_verified) {
        return {
          icon: 'CheckCircle',
          iconColor: 'text-green-500',
          text: 'Evidencia verificada',
          bgColor: 'bg-green-500/10'
        };
      } else {
        return {
          icon: 'Camera',
          iconColor: 'text-blue-500',
          text: 'Nueva evidencia subida',
          bgColor: 'bg-blue-500/10'
        };
      }
    } else if (activity?.status === 'pending') {
      return {
        icon: 'Swords',
        iconColor: 'text-orange-500',
        text: 'Desafío abierto',
        bgColor: 'bg-orange-500/10'
      };
    } else if (activity?.status === 'active') {
      return {
        icon: 'Flame',
        iconColor: 'text-red-500',
        text: 'Duelo en progreso',
        bgColor: 'bg-red-500/10'
      };
    } else {
      return {
        icon: 'Trophy',
        iconColor: 'text-yellow-500',
        text: 'Duelo completado',
        bgColor: 'bg-yellow-500/10'
      };
    }
  };

  // Get status badge
  const getStatusBadge = (activity) => {
    const statusConfig = {
      pending: { label: 'Abierto', color: 'bg-orange-500/20 text-orange-600' },
      active: { label: 'Activo', color: 'bg-red-500/20 text-red-600' },
      completed: { label: 'Completado', color: 'bg-green-500/20 text-green-600' },
      cancelled: { label: 'Cancelado', color: 'bg-gray-500/20 text-gray-600' }
    };

    const config = statusConfig?.[activity?.status] || statusConfig?.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <Icon name="Loader2" size={24} className="text-muted-foreground animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with notification badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="Activity" size={20} className="text-primary" />
          <h3 className="text-lg font-bold text-foreground">Actividad de Amigos</h3>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full"
            >
              {unreadCount}
            </motion.span>
          )}
        </div>
        {unreadCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={markAsViewed}
          >
            Marcar como visto
          </Button>
        )}
      </div>
      {/* Activity Feed */}
      {activities?.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground mb-2">No hay actividad de amigos</p>
          <p className="text-xs text-muted-foreground">Los desafíos de tus amigos aparecerán aquí</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          <AnimatePresence>
            {activities?.map((activity, index) => {
              const activityInfo = getActivityInfo(activity);
              const isNew = activity?.activity?.hasNewActivity;

              return (
                <motion.div
                  key={activity?.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 rounded-xl border transition-all cursor-pointer ${
                    isNew
                      ? 'bg-primary/5 border-primary/30 shadow-sm'
                      : 'bg-card border-border hover:border-primary/50'
                  }`}
                  onClick={() => onDuelClick?.(activity)}
                >
                  {/* Activity Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start space-x-3 flex-1">
                      {/* Creator Avatar */}
                      {activity?.creator?.avatar_url ? (
                        <LazyImage
                          src={activity?.creator?.avatar_url}
                          alt={activity?.creator?.full_name || 'User avatar'}
                          className="w-10 h-10 rounded-full"
                          sizes="40px"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {activity?.creator?.full_name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                      )}

                      {/* Activity Info */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="text-sm font-semibold text-foreground">
                            {activity?.creator?.full_name}
                          </p>
                          {activity?.opponent && (
                            <>
                              <span className="text-xs text-muted-foreground">vs</span>
                              <p className="text-sm font-semibold text-foreground">
                                {activity?.opponent?.full_name}
                              </p>
                            </>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className={`p-1 rounded-lg ${activityInfo?.bgColor}`}>
                            <Icon name={activityInfo?.icon} size={12} className={activityInfo?.iconColor} />
                          </div>
                          <span className="text-xs text-muted-foreground">{activityInfo?.text}</span>
                          <span className="text-xs text-muted-foreground">•</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(activity?.created_at), { addSuffix: true, locale: es })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Status Badge */}
                    {getStatusBadge(activity)}
                  </div>
                  {/* Challenge Details */}
                  <div className="mb-3">
                    <p className="text-sm font-medium text-foreground mb-1">{activity?.challenge_type}</p>
                    <p className="text-xs text-muted-foreground">
                      {activity?.target_value} {activity?.target_unit}
                    </p>
                  </div>
                  {/* Verification Progress */}
                  {activity?.activity?.totalProofs > 0 && (
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">Progreso de verificación</span>
                        <span className="text-xs font-medium text-foreground">
                          {activity?.activity?.verifiedProofs}/{activity?.activity?.totalProofs} verificadas
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${activity?.activity?.verificationProgress}%` }}
                          transition={{ duration: 0.5 }}
                          className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full"
                        />
                      </div>
                    </div>
                  )}
                  {/* Latest Proof Preview */}
                  {activity?.activity?.latestProof?.photo_url && (
                    <div className="mb-3">
                      <img
                        src={activity?.activity?.latestProof?.photo_url}
                        alt="Latest proof"
                        className="w-full h-32 object-cover rounded-lg"
                      />
                    </div>
                  )}
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    {activity?.activity?.canJoin ? (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleJoinDuel(activity?.id);
                        }}
                        className="flex-1"
                      >
                        <Icon name="Swords" size={14} className="mr-1" />
                        Unirse al Duelo
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e?.stopPropagation();
                          onDuelClick?.(activity);
                        }}
                        className="flex-1"
                      >
                        <Icon name="Eye" size={14} className="mr-1" />
                        Ver Detalles
                      </Button>
                    )}
                    {activity?.activity?.totalProofs > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e?.stopPropagation();
                          onDuelClick?.(activity);
                        }}
                      >
                        <Icon name="MessageSquare" size={14} className="mr-1" />
                        {activity?.activity?.totalProofs}
                      </Button>
                    )}
                  </div>
                  {/* New Activity Indicator */}
                  {isNew && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"
                    />
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default FriendDuelActivityFeed;